import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import Modal from "components/modal/Modal";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { LoadingOverlay } from "components/ui/loading-spinners";
import { LeadProvider, RegisterData, RegistrationRoleType, useGetDecryptUserQuery, usePostRegistrationMutation } from "./registrationService";
import * as Constant from "utilities/Constant";
import { emailAddress, phone, password_input, firstname, lastname, loanpurpose, schemas } from "utilities/formSchemas";
import "./registration.scss";
import { useDispatch } from "react-redux";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import { setCredentials, setLeadProvider, setUserId } from "features/authSlice";
import { ErrorType } from "utilities/error";
import { Consent } from "components/consent/Consent";
import useDesktopChecker from "hooks/useDesktopChecker";
import { setFSUserProperties, trackFSVisitedRegistrationPage } from "utilities/full-story";
import PasswordChecker from "components/password-and-confirm-password/passwordcheckerversion2/PasswordCheckerVersion2";
import homeGraphic from "assets/images/HomeGraphic.png";
import { setResendCodeDeviceId } from "pages/login/loginSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import { RegistrationLegalConsent } from "components/registration-legal-consent/Registration-legal-consent";
import Footer from "components/footer/Footer";

const formSchema = z.object({
  email: emailAddress,
  firstname,
  lastname,
  phone,
  loanpurpose,
  password_input,
});
export default function Registration() {
  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields },
    setValue,
  } = useForm({
    resolver: zodResolver(formSchema),
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDesktop = useDesktopChecker();
  const encryptedUser = searchParams.get("t")!;
  const email = searchParams.get("email")!;
  const loanId = searchParams.get("loanId") ?? searchParams.get("lid")!;
  const dealId = searchParams.get("did")!;
  const prospectId = searchParams.get("pid")!;
  const leadProviderName = searchParams.get("lpn")!;
  const losId = searchParams.get("losId")!;
  const isEncryptedUser = !!encryptedUser;
  const isBonzoUrlParams = !!(email && loanId && leadProviderName);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const {
    data: decryptedUserDetails,
    isLoading,
    isFetching,
  } = useGetDecryptUserQuery({ encryptedUser, email, loanId, leadProviderName, dealId, losId, prospectId }, { skip: !(isEncryptedUser || isBonzoUrlParams) });

  useEffect(() => {
    if (decryptedUserDetails) {
      const { ex, fn, ln, em, ph, lp, lpn, rt } = decryptedUserDetails;
      if (!ex) {
        if (lpn) {
          dispatch(setLeadProvider(lpn));
        }
        setIsBtnDisabled((prevState) => !prevState);
        setValue("firstname", fn);
        setValue("lastname", ln);
        setValue("email", em);
        setValue("phone", ph.replace(/-/g, ""));
        setValue("loanpurpose", lp);
        const isBonzoSpousalCoBorrower =
          leadProviderName?.toLowerCase() === LeadProvider.Bonzo.toLowerCase() &&
          rt?.toLowerCase() === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
        setIsBonzoSpouse(isBonzoSpousalCoBorrower);
      } else {
        navigate("/", { replace: true, state: decryptedUserDetails });
      }
    }
  }, [decryptedUserDetails]);


  useEffect(() => {
    if (!decryptedUserDetails) return;
    if (decryptedUserDetails.ex) return;

    const leadProvider = decryptedUserDetails.lpn ?? "Unknown";
    const loanType = decryptedUserDetails.lp ?? "Unknown";


    trackFSVisitedRegistrationPage({
      leadProvider: leadProvider,
      loanType: loanType,
    });
  }, [decryptedUserDetails]);

  // Show Tracking Pixel
  const [showPixel, setShowPixel] = useState(false);
  useEffect(() => {
    if (
      featureFlag?.ttdCodeSnippetEnabled &&
      !isLoading &&
      !isFetching &&
      (decryptedUserDetails || leadProviderName)
    ) {
      setShowPixel(true);
    }
  }, [featureFlag?.ttdCodeSnippetEnabled, isLoading, isFetching, decryptedUserDetails, leadProviderName]);

  let showModal = false;
  if (!isLoading && !isFetching && !decryptedUserDetails) {
    showModal = true;
  } else {
    setFSUserProperties(decryptedUserDetails?.em ?? "", decryptedUserDetails?.ln.toLowerCase() ?? "");
  }

  const [error, setError] = useState(0);
  const [hasPwdError, setHasPwdError] = useState(false);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [isBonzoSpouse, setIsBonzoSpouse] = useState(false);
  const [sendRequest, { isSuccess, data, isLoading: isRegistrationLoading }] = usePostRegistrationMutation();
  const dispatch = useDispatch();
  const onSubmit = (data: RegisterData) => {
    if (!hasErrors && !hasPwdError) {
      sendRequest(data)
        .unwrap()
        .catch((error: ErrorType) => {
          if (error.status == 409) {
            navigate("/", { replace: true });
          } else {
            setError(500);
          }
        });
    }
  };
  if (isSuccess && data) {
    if (isBonzoSpouse) {
      dispatch(setUserId(data.id));
      dispatch(setResendCodeDeviceId(data.deviceAuthId));
      navigate("/registrationAuth", { replace: true });
    } else {
      dispatch(
        setCredentials({
          ...data,
        })
      );
      navigate("/my-loans", { replace: true });
    }
  }
  const ispasswordFieldModified = (dirtyFields.password_input as boolean) || false;
  const hasErrors = !!Object.keys(errors).length;

  if (isRegistrationLoading) return <LoadingOverlay text="Registering..." />;
  const formatContent = (content: string[]) => {
    return (
      <ul className="registration-list">
        {content?.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    );
  };

  return (
    <>
      {/* NAF_Lead Portal_Static_6.4.25 Tracking Pixel */}
      {showPixel && (
        <img
          height="1"
          width="1"
          style={{ borderStyle: "none" }}
          alt=""
          src={`${Constant.TTD_CODE_SNIPPET_URL}/?adv=ffdysrg&ct=0:vf1o9ix&fmt=3&td1=${encodeURIComponent(
            leadProviderName || decryptedUserDetails?.lpn || "Others"
          )}&td2=&td3=${encodeURIComponent(decryptedUserDetails?.id && decryptedUserDetails.id !== "00000000-0000-0000-0000-000000000000"
            ? decryptedUserDetails.id
            : "")}`}
        />
      )}

      {(isLoading || isFetching) && <LoadingOverlay text="Loading user details..." />}
      <div className="registration">
        {showModal && (
          <Modal
            defaultBody
            defaultBodyLabel="Something went wrong!"
            defaultBodyLabelIcon={regular("triangle-exclamation")}
            defaultBodySubContent={[
              "Please open the email we sent you and click on the link so that we can try again.",
              "We apologize for the inconvenience.",
            ]}
            footer={<></>}
          />
        )}
        <div className="registration__container">
          <div className="registration__form">
            <h1 className="registration__header">{isDesktop ? `Create an account to continue your application` : `Create an account to continue`}</h1>
            {error == 500 && (
              <div className="registration__error">
                {isDesktop
                  ? Constant.RegistrationError
                  : Constant.RegistrationError.map((error) => {
                    return <p key={error}>{error}</p>;
                  })}
              </div>
            )}
            <Form
              onSubmit={handleSubmit((data) => {
                onSubmit({
                  emailaddress: data.email as string,
                  password: data.password_input as string,
                  firstname: data.firstname as string,
                  lastname: data.lastname as string,
                  loanPurpose: data.loanpurpose as string,
                  phoneNumber: data.phone as string,
                  referralSource: "",
                  referredBy: "someone",
                  inquiryId: decryptedUserDetails?.id,
                  leadProvider: isBonzoSpouse ? LeadProvider.Bonzo : decryptedUserDetails?.lpn,
                  nhDealApplicationId: decryptedUserDetails?.nhDealAppId,
                  userRole: isBonzoSpouse ? decryptedUserDetails?.rt : RegistrationRoleType.Borrower,
                  isTestLoan: decryptedUserDetails?.isTestLoan,
                  decryptPhoneNumber: decryptedUserDetails?.ph as string,
                });
              })}
            >
              <div className="row--item">
                <FormInput
                  formHandle={register("email")}
                  inputProps={{
                    name: "email",
                    placeholder: "Enter Your Email Address",
                    label: "Email Address",
                    errorMessage: errors.email?.message as string,
                    isReadOnly: true,
                  }}
                />
              </div>
              <div className={isBonzoSpouse ? "row--hidden" : "row--item"}>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <PhoneNumberInput
                      onChange={(newValue: string) => {
                        onChange(newValue);
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        setValue("phone", schemas.phone.parse(newValue));
                      }}
                      value={value as string}
                      errorMessage={errors.phone?.message as string}
                      label="Phone Number"
                      placeholder={`Enter Phone Number`}
                      isReadOnly={!featureFlag?.updatePhoneNumberEnabled}
                    />
                  )}
                />
              </div>
              <div className="row--item">
                <Controller
                  name="password_input"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <PasswordChecker
                      onChange={onChange}
                      checkErrors={ispasswordFieldModified}
                      createLabel="Create Password"
                      setHasPasswordError={setHasPwdError}
                      setBtnVisible={setIsBtnDisabled}
                      createPasswordPlaceholderName="Create password"
                    />
                  )}
                />
              </div>
              <Button title="Continue" className="registration__button" disabled={isBtnDisabled} />
            </Form>
            {featureFlag?.splitConsentEnabled && <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />}
          </div>
          {isDesktop && (
            <div className="registration__information">
              <p className="registration__text1">Better Path to Homeownership</p>
              <p className="registration__text2">Seamless, Secure, and Built for You</p>
              <p className="registration__text3">{formatContent(Constant.registrationInformation)}</p>
              <img src={homeGraphic} alt="login-graphic" className="registration__graphic" />
            </div>
          )}
        </div>
      </div>
      {!featureFlag?.splitConsentEnabled && <RegistrationLegalConsent />}
      <div className="registration__footer-section"><Footer /></div>
    </>
  );
}
